import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { stagger } from "framer-motion";
import { Star, CircleCheck } from "lucide-react";
import {
  createBetaFeedbackSchema,
  type CreateBetaFeedbackInput,
} from "@shared/validation/betaFeedback.schema";
import feedbackBubble from "../../assets/landing/feedback-bubble.webp";
import { useSubmitBetaFeedback } from "../../lib/api/useBetaFeedback";
import { Button } from "../ui/Button";
import { Field } from "../ui/Field";
import { LANDING_INPUT_CLASS } from "../../lib/forms/inputClass";
import { FOCUS_RING } from "../../lib/focusRing";
import { cn } from "../../lib/cn";
import { Scene } from "./stage/Scene";
import { Footer } from "./Footer";
import * as fx from "./stage/fx";
import type { DomStep, Timeline } from "./stage/choreography";

/**
 * Each star flashes amber as it pops in, then settles back to its real (unrated)
 * look. The overlay rests at opacity-0 in CSS: a form re-rendered after "Send
 * more feedback" (or under reduced motion) is never animated, so its resting
 * state must already be the right one.
 */
const STAR_FLASH: DomStep = {
  kind: "dom",
  target: "[data-fx=star-flash]",
  keyframes: { opacity: [0, 1, 0] },
  options: { duration: 0.6, times: [0, 0.3, 1], ease: "easeOut", delay: stagger(0.06), at: "<" },
};

/*
 * Signature: block reveal + curtain fields. A colour block sweeps the
 * headline on, then the form draws itself in top to bottom: each field slides
 * across from the left like a curtain, the five stars light up left → right,
 * the button pops, and the footer fades up last. The thank-you card (shown
 * after sending) has no fields, so those steps simply skip.
 */
const TIMELINE: Timeline = [
  fx.fade("[data-fx=eyebrow]", { duration: 0.3 }),
  fx.block("[data-fx=title]", { duration: 0.8, at: "-0.15" }),
  fx.fade("[data-fx=sub]", { at: "-0.15" }),
  fx.pop("[data-fx=bubble]", { from: 0.6, at: "-0.3" }),
  fx.fade("[data-fx=panel]", { y: 16, at: "-0.45" }),
  fx.slide("[data-fx=field]", { x: -36, duration: 0.45, stagger: 0.08, at: "-0.3" }),
  fx.slide("[data-fx=rating]", { x: -36, duration: 0.45, at: "-0.3" }),
  fx.pop("[data-fx=star]", { stagger: 0.06, at: "-0.3" }),
  STAR_FLASH,
  fx.slide("[data-fx=message]", { x: -36, duration: 0.45, at: "-0.35" }),
  fx.pop("[data-fx=submit]", { from: 0.6, at: "-0.25" }),
  fx.fade("[data-fx=footer]", { at: "-0.2" }),
];

const RATINGS = [1, 2, 3, 4, 5] as const;

/**
 * Public beta feedback. Name, email, and rating are all optional — the lower
 * the barrier, the more people who bounced off the product actually tell us why.
 */
export function BetaFeedback() {
  const submit = useSubmitBetaFeedback();
  const [rating, setRating] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const thanksRef = useRef<HTMLHeadingElement>(null);
  const refocusForm = useRef(false);

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm<CreateBetaFeedbackInput>({
    resolver: zodResolver(createBetaFeedbackSchema),
    defaultValues: { name: null, email: null, rating: null, feedback: "" },
  });

  // The form and the thank-you card replace each other, which would drop
  // keyboard focus on the floor; hand it to whichever one just appeared.
  useEffect(() => {
    if (submit.isSuccess) {
      thanksRef.current?.focus({ preventScroll: true });
    } else if (refocusForm.current) {
      refocusForm.current = false;
      setFocus("name");
    }
  }, [submit.isSuccess, setFocus]);

  return (
    <Scene id="feedback" label="Feedback" timeline={TIMELINE} className="bg-sky-50/60">
      {/* Backdrop: soft colour fields and a whisper of grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute -right-32 top-1/3 h-[26rem] w-[26rem] rounded-full bg-indigo-100/60 blur-3xl" />
        <div className="absolute bottom-24 left-1/4 h-72 w-72 rounded-full bg-amber-100/50 blur-3xl" />
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
      </div>

      <div className="mx-auto grid w-full max-w-6xl flex-1 content-center items-center gap-6 px-6 pb-8 pt-20 sm:gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14 lg:pb-10">
        {/* Copy */}
        <div>
          <p data-fx="eyebrow" className="text-sm font-semibold uppercase tracking-wide text-sky-700">
            Beta
          </p>
          <h2
            data-fx="title"
            className="mt-2 font-display text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl"
          >
            {/* The block sweeps across exactly the text's box, not the column, and
                sits in FRONT of it: the text appears hidden under the block and is
                uncovered as the block exits. */}
            <span className="relative inline-block">
              <span
                data-block
                aria-hidden="true"
                className="absolute -inset-x-1.5 -bottom-1.5 -top-0.5 z-10 rounded-lg bg-gradient-to-r from-sky-400 to-indigo-500"
              />
              <span data-block-text className="relative">
                Tell us what you think
              </span>
            </span>
          </h2>
          <p data-fx="sub" className="mt-3 max-w-md text-[15px] text-neutral-600 sm:mt-4 sm:text-lg">
            What works, what doesn't, and what's missing. Every note is read.
          </p>
          {/* Set toward the form, so the eye travels heading → bubble → form. */}
          <div data-fx="bubble" className="ml-auto mr-6 mt-2 hidden w-36 lg:block xl:mr-10 xl:w-40">
            <div className="animate-float">
              <img
                src={feedbackBubble}
                alt=""
                aria-hidden="true"
                width={360}
                height={360}
                loading="lazy"
                draggable={false}
                className="h-auto w-full select-none"
              />
            </div>
          </div>
        </div>

        {submit.isSuccess ? (
          <div
            data-fx="panel"
            className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center shadow-soft"
          >
            <CircleCheck className="mx-auto h-8 w-8 text-emerald-600" aria-hidden />
            <h3
              ref={thanksRef}
              tabIndex={-1}
              className="mt-3 font-display text-2xl font-bold tracking-tight text-emerald-900 outline-none"
            >
              Thank you
            </h3>
            <p className="mt-2 text-sm text-emerald-800">
              Your feedback helps us make Leena's List safer and more useful for every family.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-5"
              onClick={() => {
                refocusForm.current = true;
                reset();
                setRating(null);
                submit.reset();
              }}
            >
              Send more feedback
            </Button>
          </div>
        ) : (
          <form
            data-fx="panel"
            aria-label="Beta feedback"
            /* overflow-hidden: the fields draw in from behind the card's own edge, like curtains. */
            className="space-y-4 overflow-hidden rounded-3xl border border-sky-100 bg-white p-5 shadow-soft sm:space-y-5 sm:p-7"
            onSubmit={handleSubmit((values) => submit.mutate({ ...values, rating }))}
            noValidate
          >
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
              <div data-fx="field">
                <Field label="Name (optional)" htmlFor="feedback-name" error={errors.name?.message}>
                  <input
                    id="feedback-name"
                    type="text"
                    autoComplete="name"
                    {...register("name")}
                    className={LANDING_INPUT_CLASS}
                  />
                </Field>
              </div>
              <div data-fx="field">
                <Field
                  label="Email (optional)"
                  htmlFor="feedback-email"
                  error={errors.email?.message}
                >
                  <input
                    id="feedback-email"
                    type="email"
                    autoComplete="email"
                    {...register("email")}
                    className={LANDING_INPUT_CLASS}
                  />
                </Field>
              </div>
            </div>

            {/* Radio group, not buttons: arrow keys move between stars and the
                current choice is announced, which a row of <button>s wouldn't do. */}
            <fieldset data-fx="rating" onMouseLeave={() => setHovered(null)}>
              <legend className="text-sm font-medium text-neutral-700">
                Overall experience (optional)
              </legend>
              <div className="-ml-2.5 mt-1 flex items-center sm:-ml-1.5">
                {RATINGS.map((value) => {
                  const filled = (hovered ?? rating ?? 0) >= value;
                  return (
                    <label
                      key={value}
                      data-fx="star"
                      className={cn(
                        // The radio itself is sr-only, so the ring follows its keyboard focus.
                        "relative cursor-pointer rounded-lg p-2.5 transition-colors sm:p-1.5",
                        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-sky-500 has-[:focus-visible]:ring-offset-2",
                      )}
                      onMouseEnter={() => setHovered(value)}
                    >
                      <input
                        type="radio"
                        name="rating"
                        value={value}
                        checked={rating === value}
                        onChange={() => setRating(value)}
                        className="sr-only"
                      />
                      <span className="sr-only">
                        {value} star{value === 1 ? "" : "s"}
                      </span>
                      <span className="relative block">
                        <Star
                          aria-hidden
                          className={cn(
                            "h-6 w-6 transition-colors",
                            filled ? "fill-amber-400 text-amber-400" : "text-neutral-300",
                          )}
                        />
                        <Star
                          aria-hidden
                          data-fx="star-flash"
                          className="pointer-events-none absolute inset-0 h-6 w-6 fill-amber-400 text-amber-400 opacity-0"
                        />
                      </span>
                    </label>
                  );
                })}
                {rating !== null && (
                  <button
                    type="button"
                    onClick={() => setRating(null)}
                    className={cn(
                      "ml-2 min-h-[44px] rounded-lg px-2 text-xs text-neutral-500 hover:text-neutral-700 sm:min-h-0 sm:py-1",
                      FOCUS_RING,
                    )}
                  >
                    Clear
                  </button>
                )}
              </div>
            </fieldset>

            <div data-fx="message">
              <Field
                label="Your feedback"
                htmlFor="feedback-body"
                error={errors.feedback?.message}
              >
                <textarea
                  id="feedback-body"
                  rows={4}
                  placeholder="What did you love? What was confusing? Anything broken?"
                  {...register("feedback")}
                  className={LANDING_INPUT_CLASS}
                />
              </Field>
            </div>

            {submit.isError && (
              <p role="alert" className="text-sm font-medium text-red-700">
                {submit.error.code === "RATE_LIMITED"
                  ? "You've sent a few already — please try again a bit later."
                  : "Something went wrong sending that. Please try again."}
              </p>
            )}

            <div data-fx="submit" className="w-fit">
              <Button type="submit" isLoading={submit.isPending}>
                {submit.isPending ? "Sending…" : "Send feedback"}
              </Button>
            </div>
          </form>
        )}
      </div>

      <Footer />
    </Scene>
  );
}
